import { createBrowserRouter, Navigate, useParams, useLocation } from 'react-router-dom'
import { RootLayout } from '@/components/layout/RootLayout'
import { AppLayout } from '@/components/layout/AppLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useFF } from '@/platform/feature-flags'

function VideoEditorPage() {
  const classicEditor = useFF('classic_editor')
  return classicEditor ? <CreateOldPage /> : <CreatePage />
}

function LegacyProjectRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search, hash } = useLocation()
  return <Navigate to={`/video/${id ?? ''}${search}${hash}`} replace />
}

function LegacyAdminProjectRedirect({ kind }: { kind: 'detail' | 'inspect' | 'replay' }) {
  const { id } = useParams<{ id: string }>()
  const { search, hash } = useLocation()
  return <Navigate to={`/admin/videos/${kind}/${id ?? ''}${search}${hash}`} replace />
}

// Public routes
import HomePage from '@/routes/index'
import OldHomePage from '@/routes/oldhome'
import AuthCallbackPage from '@/routes/auth'
import PricingPage from '@/routes/pricing'
import IntegrationsPage from '@/routes/integrations'
import TermsPage from '@/routes/terms'
import PrivacyPage from '@/routes/privacy'
import ContentPolicyPage from '@/routes/content-policy'
import SharePage from '@/routes/share/[id]'
import SharedConversationPage from '@/routes/share/chat/[id]'
import EmbedPage from '@/routes/embed/[id]'
import DownloadMacPage from '@/routes/download-for-mac'
import DownloadWindowsPage from '@/routes/download-for-windows'
import UpgradePage from '@/routes/upgrade'
import CLIPage from '@/routes/cli'
import SolutionsPage from '@/routes/solutions'
import CliAuthPage from '@/routes/cli-auth'
import XLandingPage from '@/routes/x'
import DocumentationPage from '@/routes/documentation/index'
import DocsPage from '@/routes/docs/index'

// Protected routes
import StudioPage from '@/routes/projects/index'
import CreatePage from '@/routes/video/[id]'
import CreateOldPage from '@/routes/video2/[id]'
import SettingsRedirect from '@/routes/settings/index'
import SupportPage from '@/routes/support/index'
import TemplatesPage from '@/routes/templates/index'
import TrashPage from '@/routes/trash/index'
import AssetsPage from '@/routes/assets/index'
import SkillsPage from '@/routes/skills/index'
import UploadsPage from '@/routes/uploads/index'
import AvatarsPage from '@/routes/avatars/index'
import VoicesPage from '@/routes/voices/index'

// Admin routes
import AdminUsersPage from '@/routes/admin/users/index'
import AdminProjectsPage from '@/routes/admin/projects/index'
import AdminUserDetailPage from '@/routes/admin/users/[id]/index'
import AdminProfitLossPage from '@/routes/admin/profit-loss/index'
import AdminSupportPage from '@/routes/admin/support/index'

import AdminDevelopersPage from '@/routes/admin/developers/index'
import AdminObservabilityPage from '@/routes/admin/observability/index'
import AdminAgentPage from '@/routes/admin/agent/index'
import AdminProjectDetailPage from '@/routes/admin/projects/detail/[id]'
import AdminProjectInspectPage from '@/routes/admin/projects/inspect/[id]'
import AdminProjectReplayPage from '@/routes/admin/projects/replay/[id]'
import PendingPage from '@/routes/pending/index'
import NuxPage from '@/routes/nux'
import FlagsPage from '@/routes/flags/index'
import AdminModelConfigPage from '@/routes/admin/model-config/index'
import AdminEvalsPage from '@/routes/admin/evals/index'
import AdminEventsPage from '@/routes/admin/events/index'
import AdminAnalyticsPage from '@/routes/admin/analytics/index'
import AdminMusicListPage from '@/routes/admin/musiclist/index'
import AdminDirectoriesPage from '@/routes/admin/directories/index'
import AdminDirectoryDetailPage from '@/routes/admin/directories/[slug]/index'
import AdminDirectoryQueuePage from '@/routes/admin/directory-queue/index'
import AdminDirectoryTemplatePage from '@/routes/admin/directory-template/index'
import AdminVoiceLabPage from '@/routes/admin/voice-lab/index'
import AdminRenderGpusPage from '@/routes/admin/render-gpus/index'
import AdminFeedbackPage from '@/routes/admin/feedback/index'
import AdminModFeedbackPage from '@/routes/admin/mod-feedback/index'
import AdminIssuesPage from '@/routes/admin/issues/index'
import AdminIssueDetailPage from '@/routes/admin/issues/[issueNumber]'
import AdminAnalysisQueuePage from '@/routes/admin/analysis-queue/index'
import AdminInvestorDetailPage from '@/routes/admin/investors/[slug]/index'
import AdminFundDetailPage from '@/routes/admin/investors/funds/[slug]/index'
import AdminMetaAnalysisRunsPage from '@/routes/admin/meta-analysis-runs/index'
import AdminCronjobsPage from '@/routes/admin/cronjobs/index'
import AdminPipelinePage from '@/routes/admin/pipeline/index'
import AdminFeedPage from '@/routes/admin/feed/index'
import AdminActivityPage from '@/routes/admin/activity/index'
import AdminRampPage from '@/routes/admin/ramp/index'
import AdminConversionPage from '@/routes/admin/conversion/index'
import AdminSessionsPage from '@/routes/admin/sessions/index'
import AdminSessionReplayPage from '@/routes/admin/sessions/[id]/index'
import AdminClustersPage from '@/routes/admin/clusters/index'
import AdminPaymentExamplesPage from '@/routes/admin/payment-examples/index'

export const router = createBrowserRouter([
  // Embed route — no layout wrapper, bare video player
  {
    path: '/embed/:id',
    element: <ErrorBoundary fallback={null}><EmbedPage /></ErrorBoundary>,
  },
  {
    element: <RootLayout />,
    children: [
      // Public routes
      {
        path: '/oldhome',
        element: <OldHomePage />,
      },
      {
        path: '/pricing',
        element: <PricingPage />,
      },
      {
        path: '/integrations',
        element: <IntegrationsPage />,
      },
      {
        path: '/terms',
        element: <TermsPage />,
      },
      {
        path: '/privacy',
        element: <PrivacyPage />,
      },
      {
        path: '/content-policy',
        element: <ContentPolicyPage />,
      },
      {
        path: '/share/dolphin-ocean-journey',
        element: <SharePage demo />,
      },
      {
        path: '/share/chat/:id',
        element: <SharedConversationPage />,
      },
      {
        path: '/share/:id',
        element: <SharePage />,
      },
      {
        path: '/download-for-mac',
        element: <DownloadMacPage />,
      },
      {
        path: '/download-for-windows',
        element: <DownloadWindowsPage />,
      },
      {
        path: '/upgrade',
        element: <UpgradePage />,
      },
      {
        path: '/cli',
        element: <CLIPage />,
      },
      {
        path: '/solutions',
        element: <SolutionsPage />,
      },
      {
        path: '/cli-auth',
        element: <CliAuthPage />,
      },
      {
        path: '/x',
        element: <XLandingPage />,
      },
      {
        path: '/docs',
        element: <DocsPage />,
      },
      {
        path: '/docs/:slug',
        element: <DocsPage />,
      },
      {
        path: '/documentation',
        element: <Navigate to="/docs" replace />,
      },
      {
        path: '/documentation/:slug',
        element: <DocumentationPage />,
      },
      {
        path: '/nux',
        element: <NuxPage />,
      },
      // OAuth callback landing — handles ?code= from Google, routes to the
      // visitor's newly-merged workflow (or /). The only sign-in UI is the
      // AuthModal popup, surfaced by navigating to `/#signin` (or any path
      // ending in that hash — see AppModalsProvider).
      {
        path: '/auth',
        element: <AuthCallbackPage />,
      },
      {
        path: '/login',
        element: <Navigate to="/#signin" replace />,
      },
      {
        path: '/signup',
        element: <Navigate to="/#signin" replace />,
      },
      // App routes (with AppLayout). AppLayout allows anonymous visitors on
      // `/` and `/support` — see its internal carve-out.
      {
        element: <AppLayout />,
        children: [
          {
            path: '/',
            element: <HomePage />,
          },
          {
            path: '/library',
            element: <StudioPage />,
          },
          {
            path: '/videos',
            element: <Navigate to="/library" replace />,
          },
          {
            path: '/projects',
            element: <Navigate to="/library" replace />,
          },
          {
            path: '/studio',
            element: <Navigate to="/library" replace />,
          },
          {
            path: '/video/new',
            element: <Navigate to="/" replace />,
          },
          {
            path: '/video/:id',
            element: <VideoEditorPage />,
          },
          {
            path: '/video2/:id',
            element: <CreateOldPage />,
          },
          {
            path: '/project/:id',
            element: <LegacyProjectRedirect />,
          },
          {
            path: '/settings',
            element: <SettingsRedirect />,
          },
          {
            path: '/support',
            element: <SupportPage />,
          },
          {
            path: '/assets',
            element: <AssetsPage />,
          },
          {
            path: '/skills',
            element: <SkillsPage />,
          },
          {
            path: '/media',
            element: <Navigate to="/assets" replace />,
          },
          {
            path: '/templates',
            element: <TemplatesPage />,
          },
          {
            path: '/trash',
            element: <TrashPage />,
          },
          {
            path: '/uploads',
            element: <UploadsPage />,
          },
          {
            path: '/avatars',
            element: <AvatarsPage />,
          },
          {
            path: '/voices',
            element: <VoicesPage />,
          },
          {
            path: '/admin',
            element: <Navigate to="/admin/conversion" replace />,
          },
          {
            path: '/admin/users',
            element: <AdminUsersPage />,
          },
          {
            path: '/admin/users/:id',
            element: <AdminUserDetailPage />,
          },
          {
            path: '/admin/videos',
            element: <AdminProjectsPage />,
          },
          {
            path: '/admin/projects',
            element: <Navigate to="/admin/videos" replace />,
          },
          {
            path: '/admin/profit-loss',
            element: <AdminProfitLossPage />,
          },
          {
            path: '/admin/conversion',
            element: <AdminConversionPage />,
          },
          {
            path: '/admin/google-ads',
            element: <Navigate to="/admin/conversion" replace />,
          },
          {
            path: '/admin/attribution',
            element: <Navigate to="/admin/profit-loss?tab=organic-attribution" replace />,
          },
          {
            path: '/admin/attribution/paid',
            element: <Navigate to="/admin/profit-loss?tab=paid-attribution" replace />,
          },
          {
            path: '/admin/attribution/organic',
            element: <Navigate to="/admin/profit-loss?tab=organic-attribution" replace />,
          },
{
            path: '/admin/support',
            element: <AdminSupportPage />,
          },
          {
            path: '/admin/devtools',
            element: <AdminDevelopersPage />,
          },
          {
            path: '/admin/developers',
            element: <Navigate to="/admin/devtools" replace />,
          },
          {
            path: '/admin/fal-requests',
            element: <PendingPage />,
          },
{
            path: '/admin/observability',
            element: <AdminObservabilityPage />,
          },
          {
            path: '/admin/agent',
            element: <AdminAgentPage />,
          },
          {
            path: '/admin/sessions',
            element: <AdminSessionsPage />,
          },
          {
            path: '/admin/sessions/:id',
            element: <AdminSessionReplayPage />,
          },
          {
            path: '/admin/videos/detail/:id',
            element: <AdminProjectDetailPage />,
          },
          {
            path: '/admin/videos/inspect/:id',
            element: <AdminProjectInspectPage />,
          },
          {
            path: '/admin/videos/replay/:id',
            element: <AdminProjectReplayPage />,
          },
          {
            path: '/admin/projects/detail/:id',
            element: <LegacyAdminProjectRedirect kind="detail" />,
          },
          {
            path: '/admin/projects/inspect/:id',
            element: <LegacyAdminProjectRedirect kind="inspect" />,
          },
          {
            path: '/admin/projects/replay/:id',
            element: <LegacyAdminProjectRedirect kind="replay" />,
          },
          {
            path: '/admin/llm',
            element: <Navigate to="/admin/profit-loss?tab=agent" replace />,
          },
{
            path: '/admin/model-config',
            element: <AdminModelConfigPage />,
          },
          {
            path: '/admin/evals',
            element: <AdminEvalsPage />,
          },
          {
            path: '/admin/events',
            element: <AdminEventsPage />,
          },
          {
            path: '/admin/experiments',
            element: <Navigate to="/admin/flags" replace />,
          },
          {
            path: '/admin/analytics',
            element: <AdminAnalyticsPage />,
          },
          {
            path: '/admin/musiclist',
            element: <AdminMusicListPage />,
          },
          {
            path: '/admin/directories',
            element: <AdminDirectoryQueuePage />,
          },
          {
            path: '/admin/directories-old',
            element: <AdminDirectoriesPage />,
          },
          {
            path: '/admin/directories/:slug',
            element: <AdminDirectoryDetailPage />,
          },
          {
            path: '/admin/directory-queue',
            element: <Navigate to="/admin/directories" replace />,
          },
          {
            path: '/admin/directory-template',
            element: <AdminDirectoryTemplatePage />,
          },
          {
            path: '/admin/voice-lab',
            element: <AdminVoiceLabPage />,
          },
          {
            path: '/admin/render-gpus',
            element: <AdminRenderGpusPage />,
          },
          {
            path: '/admin/feedback',
            element: <AdminFeedbackPage />,
          },
          {
            path: '/admin/mod-feedback',
            element: <AdminModFeedbackPage />,
          },
          {
            path: '/admin/issues',
            element: <AdminIssuesPage />,
          },
          {
            path: '/admin/issues/:issueNumber',
            element: <AdminIssueDetailPage />,
          },
          {
            path: '/admin/investors',
            element: <Navigate to="/admin/profit-loss?tab=investors" replace />,
          },
          {
            path: '/admin/investors/funds/:slug',
            element: <AdminFundDetailPage />,
          },
          {
            path: '/admin/investors/:slug',
            element: <AdminInvestorDetailPage />,
          },
          {
            path: '/admin/meta-analysis-runs',
            element: <AdminMetaAnalysisRunsPage />,
          },
          {
            path: '/admin/pipeline',
            element: <AdminPipelinePage />,
          },
          {
            path: '/admin/feed',
            element: <AdminFeedPage />,
          },
          {
            path: '/admin/cronjobs',
            element: <AdminCronjobsPage />,
          },
          {
            path: '/admin/analysis-queue',
            element: <AdminAnalysisQueuePage />,
          },
          {
            path: '/admin/activity',
            element: <AdminActivityPage />,
          },
          {
            path: '/admin/ramp',
            element: <AdminRampPage />,
          },
          {
            path: '/admin/flags',
            element: <FlagsPage />,
          },
          {
            path: '/admin/flags-sandbox',
            element: <Navigate to="/admin/flags" replace />,
          },
          {
            path: '/admin/sandbox',
            element: <Navigate to="/admin/devtools?tab=sandbox" replace />,
          },
          {
            path: '/admin/clusters',
            element: <AdminClustersPage />,
          },
          {
            path: '/admin/payment-examples',
            element: <AdminPaymentExamplesPage />,
          },
        ],
      },
    ],
  },
])
